import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';

import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import EditablePictureContainer from '~components/generic/editable-picture/EditablePictureContainer';
import { Hint } from '~components/generic/editor/Hint/Hint';
import { ICrazyModuleContainerClassNamesConstruct, IEntityComponentProps } from '~interfaces/props';
import { getRelatedEntityIds } from '~services/entity-helpers';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import { AddAnnotation } from './AddAnnotation';
import { AnnotationsCanvasContainer } from './AnnotationsCanvasContainer';
import { AnnotationsListContainer } from './AnnotationsListContainer';
import { Annotation, splitRawAnnotation } from './AnnotationUtils';
import { DivAnnotations, DivImage, DivLayout } from './LayoutComponents';
import styles from './ModuleAnnotationsStyles.css';

export const ModuleAnnotations = ({
  data,
  uploadFileAction,
  editing,
  className,
  onChange,
  isEditing,
  entityDef,
}: IEntityComponentProps) => {
  const [focusMarker, setFocusMarker] = useState<string>(null);
  const [hoverMarker, setHoverMarker] = useState<string>(null);
  const handleOnMouseOver = useCallback((id: string) => setHoverMarker(id), []);
  const handleOnMouseOut = useCallback((_: string) => setHoverMarker(null), []);
  const handleOnClick = useCallback((id: string) => setFocusMarker(focusMarker === id ? null : id), [focusMarker]);
  const annotationItems: Annotation[] = useMemo(
    () =>
      ((data.attributes.field_annotations_items || []) as string[])
        .map(item => {
          return splitRawAnnotation(item);
        })
        .filter(item => !!item),
    [data],
  );

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // ClassName generation
  const layout: number = parseInt(data.attributes.field_annotations_layout, 10) || 0;
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx('relative px-0', { ...className.container });
  const clsPicture = {
    [styles.picture]: true,
  };

  // Get ImageId
  const imageId = getRelatedEntityIds(data, 'field_image')[0];
  const hasImage = Boolean(imageId);

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className={clsContainer}>
        <div className="row c-module__header">
          <div className="col col-12">
            <EditableField
              hide={data.attributes.field_hide_title}
              entityType={data.type}
              fieldName="attributes.field_title.value"
              data={data.attributes.field_title.value}
              editing={editing}
              onChange={onChange}
            />
          </div>
          <div className="col col-md-8">
            <EditableField
              hide={data.attributes.field_hide_text}
              entityType={data.type}
              fieldName="attributes.field_text.value"
              data={data.attributes.field_text.value}
              editing={editing}
              onChange={onChange}
              className="u-margin-top-sm u-margin-bottom-lg o-field-text"
            />
          </div>
        </div>
        <DivLayout layout={layout}>
          <DivImage layout={layout}>
            <div className="relative">
              <EditablePictureContainer
                className={clsPicture as unknown as ICrazyModuleContainerClassNamesConstruct}
                showWhenEmpty={true}
                entityType={data.type}
                fieldName="relationships.field_image"
                entityId={imageId}
                editing={editing}
                proportions="1:1"
                onChangeImage={onChange}
                onUpload={uploadFileAction}
                placeholder={isEditing && entityDef.fields['relationships.field_image'].options.placeholder}
                showOriginal={true}
              />
              {hasImage && (
                <AnnotationsCanvasContainer
                  editing={editing}
                  entity={data}
                  onChange={onChange}
                  onClick={handleOnClick}
                  onMouseOut={handleOnMouseOut}
                  onMouseOver={handleOnMouseOver}
                  focusMarkerId={hoverMarker || focusMarker}
                  items={annotationItems}
                />
              )}
            </div>
          </DivImage>
          <DivAnnotations layout={layout}>
            {hasImage ? (
              <>
                <AnnotationsListContainer
                  onClick={handleOnClick}
                  onMouseOut={handleOnMouseOut}
                  onMouseOver={handleOnMouseOver}
                  editing={editing}
                  entity={data}
                  onChange={onChange}
                  focusMarkerId={hoverMarker || focusMarker}
                  items={annotationItems}
                />
                <AddAnnotation editing={editing} onChange={onChange} />
              </>
            ) : (
              <Hint text="You need to upload an image before you can add annotations." />
            )}
          </DivAnnotations>
        </DivLayout>
      </div>
    </div>
  );
};

export default ModuleAnnotations;
